"use client";

import { useMemo } from "react";
import { formatFundingAmountLabel, FUNDING_AMOUNT_PRESETS } from "@/features/search/lib/funding-filters";
import {
  FilterFieldContainer,
  FilterFieldContent,
  FilterFieldLabel,
} from "../shared";
import { FilterSelectInput, type FilterSelectInputOption } from "./select-input";

type Amount = number | undefined;

const NONE = "__none__";

function amountOptions(noneLabel: string, current: Amount[]): FilterSelectInputOption<string>[] {
  // Saved searches and the assistant can set amounts that aren't presets;
  // keep them selectable so the control shows what's actually applied.
  const values = [...new Set([...FUNDING_AMOUNT_PRESETS, ...current.filter((v): v is number => v != null)])]
    .sort((a, b) => a - b);
  return [
    { value: NONE, label: noneLabel },
    ...values.map((v) => ({ value: String(v), label: formatFundingAmountLabel(v) })),
  ];
}

export function FilterFundingRangeInput({
  min,
  max,
  onRangeChange,
}: {
  min: Amount;
  max: Amount;
  onRangeChange: (min: Amount, max: Amount) => void;
}) {
  const minOptions = useMemo(() => amountOptions("No Min", [min, max]), [min, max]);
  const maxOptions = useMemo(() => amountOptions("No Max", [min, max]), [min, max]);

  const onMinChange = (raw: string) => {
    const next = raw === NONE ? undefined : Number(raw);
    onRangeChange(next, next != null && max != null && next > max ? next : max);
  };

  const onMaxChange = (raw: string) => {
    const next = raw === NONE ? undefined : Number(raw);
    onRangeChange(next != null && min != null && next < min ? next : min, next);
  };

  return (
    <FilterFieldContainer>
      <FilterFieldLabel>Funding Raised (USD)</FilterFieldLabel>
      <FilterFieldContent>
        <div className="flex w-full min-w-0 items-center">
          <span className="mr-1.5 shrink-0 whitespace-nowrap text-sm font-normal text-contrast/80">Between</span>
          <div className="min-w-0 flex-1" aria-label="Min Funding Raised (USD)">
            <FilterSelectInput
              options={minOptions}
              value={min != null ? String(min) : NONE}
              onChange={onMinChange}
              placeholder="No Min"
            />
          </div>
          <span className="mx-1.5 shrink-0 whitespace-nowrap text-sm font-normal text-contrast/80">and</span>
          <div className="min-w-0 flex-1" aria-label="Max Funding Raised (USD)">
            <FilterSelectInput
              options={maxOptions}
              value={max != null ? String(max) : NONE}
              onChange={onMaxChange}
              placeholder="No Max"
            />
          </div>
        </div>
      </FilterFieldContent>
    </FilterFieldContainer>
  );
}
